import { isStaleIdentityError } from './identityEpoch'

/**
 * A 429, 408, 5xx or a network/timeout failure on a session-maintenance call
 * (/auth/me, /auth/refresh) says nothing about whether the session is valid.
 * Only a definitive rejection (401) may end it. Everything here exists so the
 * auth store can tell the two apart and retry the transient kind.
 */

/** Total attempts (first try included) for a transient auth call. */
export const TRANSIENT_RETRY_MAX_ATTEMPTS = 3
/** Backoff before the first retry when the server gave no Retry-After; doubles each retry. */
export const TRANSIENT_RETRY_BASE_DELAY_MS = 500
/** Upper bound on any single wait, including a server-supplied Retry-After. */
export const TRANSIENT_RETRY_MAX_DELAY_MS = 30_000

type StatusShape = { status?: unknown; response?: { status?: unknown; headers?: unknown; data?: unknown } }

export function errorStatus(err: unknown): number | undefined {
  const e = err as StatusShape | undefined
  const status = typeof e?.status === 'number' ? e.status : e?.response?.status
  return typeof status === 'number' ? status : undefined
}

/** True for failures that do not prove the session is invalid. */
export function isTransientAuthError(err: unknown): boolean {
  if (!err || isStaleIdentityError(err)) return false
  const status = errorStatus(err)
  if (status !== undefined) return status === 429 || status === 408 || status >= 500
  // fetch rejects with TypeError on a network failure and with an
  // AbortError/TimeoutError DOMException when the request timeout fires.
  if (err instanceof TypeError) return true
  const name = (err as { name?: unknown }).name
  return name === 'AbortError' || name === 'TimeoutError'
}

/** Server-requested wait in ms from Retry-After (seconds or HTTP-date), else the body's retry_after seconds. */
export function retryAfterMs(err: unknown, now: number = Date.now()): number | undefined {
  const resp = (err as StatusShape | undefined)?.response
  const headers = resp?.headers as Record<string, string> | undefined
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After']
  if (typeof raw === 'string' && raw.trim() !== '') {
    const secs = Number(raw)
    if (Number.isFinite(secs) && secs >= 0) return secs * 1000
    const at = Date.parse(raw)
    if (!Number.isNaN(at)) return Math.max(0, at - now)
  }
  const bodySecs = (resp?.data as { retry_after?: unknown } | undefined)?.retry_after
  if (typeof bodySecs === 'number' && Number.isFinite(bodySecs) && bodySecs >= 0) return bodySecs * 1000
  return undefined
}

/** Random extra wait, as a fraction of the delay, so tabs/callers do not retry in lockstep. Never shortens a Retry-After. */
export const TRANSIENT_RETRY_JITTER = 0.25

/** True only for HTTP 429: the limiter answered before the handler ran, so nothing was consumed server-side. */
export function isRateLimited(err: unknown): boolean {
  return !isStaleIdentityError(err) && errorStatus(err) === 429
}

export interface RetryOptions {
  maxAttempts?: number
  baseDelayMs?: number
  maxDelayMs?: number
  sleep?: (ms: number) => Promise<void>
  /** Which failures to retry; default isTransientAuthError. */
  retryable?: (err: unknown) => boolean
  /** When the server asks for a longer wait than this, stop retrying and rethrow instead of waiting. */
  giveUpAboveMs?: number
  /** Injectable for tests; default Math.random. */
  random?: () => number
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Runs `fn`, retrying only transient failures with exponential backoff that
 * honours Retry-After (capped). A non-transient error, or the last transient
 * one, is rethrown unchanged.
 */
export async function retryTransient<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const maxAttempts = opts.maxAttempts ?? TRANSIENT_RETRY_MAX_ATTEMPTS
  const base = opts.baseDelayMs ?? TRANSIENT_RETRY_BASE_DELAY_MS
  const cap = opts.maxDelayMs ?? TRANSIENT_RETRY_MAX_DELAY_MS
  const sleep = opts.sleep ?? defaultSleep
  const retryable = opts.retryable ?? isTransientAuthError
  const random = opts.random ?? Math.random
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (attempt >= maxAttempts || !retryable(err)) throw err
      const requested = retryAfterMs(err)
      if (opts.giveUpAboveMs !== undefined && requested !== undefined && requested > opts.giveUpAboveMs) throw err
      const wait = requested ?? base * 2 ** (attempt - 1)
      await sleep(Math.min(wait * (1 + TRANSIENT_RETRY_JITTER * random()), cap))
    }
  }
}
